'use client';

import type { InputProps as AntdInputProps } from 'antd';
import type { FieldValues, Path } from 'react-hook-form';

import type { ReactNode } from 'react';
import React, { useState } from 'react';

import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Input as InputAntd } from 'antd';

import { cn } from '../../cn';

type InputProps<TFieldValues extends FieldValues> = AntdInputProps & {
  id?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  addonBefore?: ReactNode;
  maxLength?: number;
  name?: Path<TFieldValues>;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  disabled?: boolean;
  value?: string;
  hasError?: boolean;
};

const Input = <TFieldValues extends FieldValues>(
  props: InputProps<TFieldValues>
) => {
  const {
    onChange,
    name,
    className,
    placeholder,
    id,
    disabled,
    type,
    addonBefore,
    maxLength,
    onBlur,
    onFocus,
    defaultValue,
    prefix,
    suffix,
    value,
    readOnly,
    onKeyUp,
    onMouseOver,
    hasError,
    onKeyDown,
    style
  } = props;

  const [passwordVisible, setPasswordVisible] = useState(false);

  if (!onChange) {
    return null;
  }

  if (type === 'password') {
    return (
      <InputAntd.Password
        style={style}
        id={id}
        placeholder={placeholder}
        name={name}
        onChange={onChange}
        defaultValue={defaultValue}
        addonBefore={addonBefore}
        prefix={prefix}
        maxLength={maxLength}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        className={className}
        visibilityToggle={{
          visible: passwordVisible,
          onVisibleChange: setPasswordVisible
        }}
        iconRender={(visible) =>
          visible ? (
            <EyeTwoTone size={15} color={'#000'} />
          ) : (
            <EyeInvisibleOutlined size={15} color={'#000'} />
          )
        }
        suffix={suffix}
        onKeyDown={onKeyDown}
      />
    );
  }

  return (
    <InputAntd
      style={style}
      id={id}
      placeholder={placeholder}
      name={name}
      onChange={onChange}
      defaultValue={defaultValue}
      addonBefore={addonBefore}
      type={type}
      prefix={prefix}
      maxLength={maxLength}
      onBlur={onBlur}
      value={value}
      disabled={disabled}
      onFocus={onFocus}
      className={cn(
        '!bg-body !rounded-sm !px-3 !py-2 h-[36px] focus:!border-[#121310] hover:!border-[#121310] focus-within:!border-[#121310] active:!border-[#121310] !outline-none !shadow-none',
        hasError && '!border-error',
        className
      )}
      suffix={suffix}
      readOnly={readOnly}
      onKeyUp={onKeyUp}
      onMouseOver={onMouseOver}
      onKeyDown={onKeyDown}
    />
  );
};

export { Input };
export type { InputProps };
